// DocumentSecurity.jsx
import React, { useState, useEffect } from "react";
import {
  getSecurityDashboardApi,
  getSecurityAlertsApi,
  getSecurityRecentActivityApi,
  sendSecurityAlertApi,
} from "../../../../api/api";
import "./DocumentSecurity.scss";

const RANGE_OPTIONS = [
  { value: "1d", label: "Today" },
  { value: "7d", label: "7 Days" },
  { value: "30d", label: "30 Days" },
  { value: "90d", label: "90 Days" },
];

const SEVERITY_CONFIG = {
  CRITICAL: { bg: "#FEF2F2", color: "#DC2626", icon: "🔴", label: "Critical" },
  HIGH: { bg: "#FFFBEB", color: "#D97706", icon: "🟠", label: "High" },
  MEDIUM: { bg: "#EFF6FF", color: "#2563EB", icon: "🟡", label: "Medium" },
  LOW: { bg: "#F9FAFB", color: "#6B7280", icon: "🟢", label: "Low" },
};

function DocumentSecurity() {
  const [range, setRange] = useState("7d");
  const [dashboard, setDashboard] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [emailingAlert, setEmailingAlert] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashRes, alertsRes, activityRes] = await Promise.all([
        getSecurityDashboardApi(range),
        getSecurityAlertsApi(),
        getSecurityRecentActivityApi(20),
      ]);
      setDashboard(dashRes.data);
      setAlerts(alertsRes.data?.alerts || []);
      setActivities(activityRes.data?.activities || []);
    } catch (err) {
      console.error("Failed to load security data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [range]);

  const handleEmailAlert = async (alertItem, e) => {
    e.stopPropagation();
    setEmailingAlert(alertItem.type + alertItem.timestamp);
    try {
      await sendSecurityAlertApi({
        type: alertItem.type,
        severity: alertItem.severity,
        message: alertItem.message,
        details: alertItem.details,
      });
      window.alert("Alert emailed to admin successfully");
    } catch (err) {
      window.alert(
        "Failed to send: " + (err.response?.data?.message || err.message),
      );
    } finally {
      setEmailingAlert(null);
    }
  };

  const stats = dashboard?.stats || {};
  const chartData = dashboard?.chartData || [];
  const classificationBreakdown = dashboard?.classificationBreakdown || [];
  const topUploaders = dashboard?.topUploaders || [];

  const statCards = [
    {
      label: "Total Uploads",
      value: stats.totalUploads || 0,
      icon: "📤",
      color: "#0062FE",
    },
    {
      label: "Total Views",
      value: stats.totalViews || 0,
      icon: "👁",
      color: "#059669",
    },
    {
      label: "Downloads",
      value: stats.totalDownloads || 0,
      icon: "⬇️",
      color: "#7C3AED",
    },
    {
      label: "Blocked Actions",
      value: stats.blockedActions || 0,
      icon: "🚫",
      color: "#D97706",
    },
    {
      label: "Viruses Detected",
      value: stats.virusDetected || 0,
      icon: "🦠",
      color: "#DC2626",
    },
    {
      label: "Access Denied",
      value: stats.accessDenied || 0,
      icon: "🔒",
      color: "#EF4444",
    },
    {
      label: "Screenshot Attempts",
      value: stats.screenshotAttempts || 0,
      icon: "📸",
      color: "#F97316",
    },
    {
      label: "Forwarded",
      value: stats.forwardedDocs || 0,
      icon: "↗️",
      color: "#06B6D4",
    },
  ];

  const maxChartValue = Math.max(
    ...chartData.map((d) => Math.max(d.uploads, d.views, d.blocked)),
    1,
  );

  if (loading) {
    return (
      <div className="ds-loading">
        <div className="ds-spinner" />
        <p>Loading security data…</p>
      </div>
    );
  }

  return (
    <div className="document-security">
      {/* Header */}
      <div className="ds-header">
        <div>
          <h1 className="ds-title">Document Security</h1>
          <p className="ds-subtitle">
            Monitor document activity, threats, and compliance
          </p>
        </div>
        <div className="ds-header-actions">
          <div className="ds-range-selector">
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className={`ds-range-btn${range === opt.value ? " active" : ""}`}
                onClick={() => setRange(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button className="ds-refresh-btn" onClick={fetchData}>
            ↺ Refresh
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="ds-kpi-grid">
        {statCards.map((stat) => (
          <div key={stat.label} className="ds-kpi-card">
            <span
              className="ds-kpi-icon"
              style={{ background: `${stat.color}15`, color: stat.color }}
            >
              {stat.icon}
            </span>
            <div>
              <p className="ds-kpi-value">{stat.value.toLocaleString()}</p>
              <p className="ds-kpi-label">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Tab Bar */}
      <div className="ds-tabs">
        {["dashboard", "alerts", "activity"].map((tab) => (
          <button
            key={tab}
            className={`ds-tab${activeTab === tab ? " active" : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === "dashboard" && "📊 Chart"}
            {tab === "alerts" &&
              `🚨 Alerts${alerts.length > 0 ? ` (${alerts.length})` : ""}`}
            {tab === "activity" && "📋 Recent Activity"}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === "dashboard" && (
        <div className="ds-tab-content">
          <div className="ds-card">
            <h3 className="ds-card-title">Activity Over Time</h3>
            <div className="ds-chart">
              {chartData.map((day, i) => (
                <div key={i} className="ds-chart-bar-group">
                  <div className="ds-chart-bars">
                    <div
                      className="ds-chart-bar uploads"
                      style={{
                        height: `${(day.uploads / maxChartValue) * 100}%`,
                      }}
                      title={`Uploads: ${day.uploads}`}
                    />
                    <div
                      className="ds-chart-bar views"
                      style={{
                        height: `${(day.views / maxChartValue) * 100}%`,
                      }}
                      title={`Views: ${day.views}`}
                    />
                    <div
                      className="ds-chart-bar blocked"
                      style={{
                        height: `${(day.blocked / maxChartValue) * 100}%`,
                      }}
                      title={`Blocked: ${day.blocked}`}
                    />
                  </div>
                  <span className="ds-chart-label">{day.date?.slice(5)}</span>
                </div>
              ))}
            </div>
            <div className="ds-chart-legend">
              <span>
                <span className="ds-legend-dot uploads" /> Uploads
              </span>
              <span>
                <span className="ds-legend-dot views" /> Views
              </span>
              <span>
                <span className="ds-legend-dot blocked" /> Blocked
              </span>
            </div>
          </div>

          <div className="ds-bottom-row">
            <div className="ds-card">
              <h3 className="ds-card-title">Classification Breakdown</h3>
              <div className="ds-classification-list">
                {classificationBreakdown.map((c) => (
                  <div
                    key={c.classification}
                    className="ds-classification-item"
                  >
                    <span className="ds-class-name">{c.classification}</span>
                    <div className="ds-class-bar-wrap">
                      <div
                        className="ds-class-bar"
                        style={{
                          width: `${(c.count / Math.max(...classificationBreakdown.map((x) => x.count), 1)) * 100}%`,
                          background:
                            c.classification === "Restricted"
                              ? "#DC2626"
                              : c.classification === "Confidential"
                                ? "#D97706"
                                : c.classification === "Internal"
                                  ? "#0062FE"
                                  : "#059669",
                        }}
                      />
                    </div>
                    <span className="ds-class-count">{c.count}</span>
                  </div>
                ))}
                {classificationBreakdown.length === 0 && (
                  <p className="ds-empty">No data</p>
                )}
              </div>
            </div>

            <div className="ds-card">
              <h3 className="ds-card-title">Top Uploaders</h3>
              <div className="ds-uploaders-list">
                {topUploaders.map((u, i) => (
                  <div key={u.userId || i} className="ds-uploader-item">
                    <span className="ds-uploader-rank">#{i + 1}</span>
                    <span className="ds-uploader-name">{u.name}</span>
                    <span className="ds-uploader-count">{u.count} files</span>
                  </div>
                ))}
                {topUploaders.length === 0 && (
                  <p className="ds-empty">No data</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alerts Tab */}
      {activeTab === "alerts" && (
        <div className="ds-tab-content">
          <div className="ds-card">
            <h3 className="ds-card-title">Security Alerts</h3>
            {alerts.length === 0 ? (
              <div className="ds-empty-state">
                <span className="ds-empty-icon">✅</span>
                <h4>No Active Alerts</h4>
                <p>
                  Your system is secure. Alerts will appear here when suspicious
                  activity is detected.
                </p>
              </div>
            ) : (
              <div className="ds-alerts-list">
                {alerts.map((alertItem, i) => {
                  const sev =
                    SEVERITY_CONFIG[alertItem.severity] || SEVERITY_CONFIG.LOW;
                  const isEmailing =
                    emailingAlert === alertItem.type + alertItem.timestamp;
                  return (
                    <div
                      key={i}
                      className={`ds-alert-card severity-${alertItem.severity?.toLowerCase()}`}
                    >
                      <div className="ds-alert-card-left">
                        <span className="ds-alert-icon">{sev.icon}</span>
                      </div>
                      <div className="ds-alert-card-body">
                        <div className="ds-alert-card-top">
                          <span
                            className="ds-alert-badge"
                            style={{ background: sev.bg, color: sev.color }}
                          >
                            {sev.label}
                          </span>
                          <span className="ds-alert-type-text">
                            {alertItem.type.replace(/_/g, " ")}
                          </span>
                          <span className="ds-alert-time-text">
                            {new Date(alertItem.timestamp).toLocaleString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )}
                          </span>
                        </div>
                        <p className="ds-alert-message-text">
                          {alertItem.message}
                        </p>
                        {alertItem.details && (
                          <div className="ds-alert-details">
                            {alertItem.details.fileName && (
                              <span className="ds-alert-detail-tag">
                                📄 {alertItem.details.fileName}
                              </span>
                            )}
                            {alertItem.details.mediaId && (
                              <span className="ds-alert-detail-tag">
                                🆔 {alertItem.details.mediaId.slice(0, 8)}...
                              </span>
                            )}
                            {alertItem.details.count && (
                              <span className="ds-alert-detail-tag">
                                📊 {alertItem.details.count} times
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="ds-alert-card-right">
                        <button
                          className="ds-email-btn"
                          onClick={(e) => handleEmailAlert(alertItem, e)}
                          disabled={isEmailing}
                          title="Email alert to admin"
                        >
                          {isEmailing ? "⏳" : "📧"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Activity Tab */}
      {activeTab === "activity" && (
        <div className="ds-tab-content">
          <div className="ds-card">
            <h3 className="ds-card-title">Recent Activity</h3>
            {activities.length === 0 ? (
              <div className="ds-empty-state">
                <span className="ds-empty-icon">📋</span>
                <h4>No Recent Activity</h4>
                <p>
                  Activity logs will appear here as users interact with
                  documents.
                </p>
              </div>
            ) : (
              <div className="ds-activity-table-wrap">
                <table className="ds-activity-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Action</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activities.map((a) => (
                      <tr key={a.id}>
                        <td>
                          <div className="ds-user-cell">
                            <span className="ds-user-avatar">
                              {(a.actor?.fullName || "S")[0].toUpperCase()}
                            </span>
                            <span>{a.actor?.fullName || "System"}</span>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`ds-action-badge action-${a.action?.toLowerCase()}`}
                          >
                            {a.action?.replace(/_/g, " ")}
                          </span>
                        </td>
                        <td className="ds-time-cell">
                          {new Date(a.createdAt).toLocaleString("en-US", {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DocumentSecurity;
